import { useEffect, useState } from "react";

import appliedlabsLogo from "@/assets/logos-web/appliedlabs-240.webp.asset.json";
import autohaulerexchangeLogo from "@/assets/logos-web/autohaulerexchange-240.webp.asset.json";
import candidhealthLogo from "@/assets/logos-web/candidhealth-240.webp.asset.json";
import datalignadvisoryLogo from "@/assets/logos-web/datalignadvisory-240.webp.asset.json";
import nationwidepowerLogo from "@/assets/logos-web/nationwidepower-240.webp.asset.json";
import posterityhealthLogo from "@/assets/logos-web/posterityhealth-240.webp.asset.json";
import relaycommerceLogo from "@/assets/logos-web/relaycommerce-240.webp.asset.json";
import seraphicsecurityLogo from "@/assets/logos-web/seraphicsecurity-240.webp.asset.json";
import uniforms2youLogo from "@/assets/logos-web/uniforms2you-300.webp.asset.json";
import hspLogo from "@/assets/logos-web/hsp-240.webp.asset.json";
import prospectdbLogo from "@/assets/logos-web/prospectdb-240.webp.asset.json";

const LOGOS: {
  src: string;
  width: number;
  height: number;
  alt: string;
  imgClass?: string;
  permissionDate?: string;
}[] = [
  { src: appliedlabsLogo.url, width: 240, height: 75, alt: "Applied Labs logo" },
  { src: autohaulerexchangeLogo.url, width: 240, height: 55, alt: "Auto Hauler Exchange logo" },
  { src: candidhealthLogo.url, width: 240, height: 61, alt: "Candid Health logo" },
  { src: hspLogo.url, width: 240, height: 64, alt: "HSP logo" },
  { src: prospectdbLogo.url, width: 240, height: 40, alt: "Prospect DB logo" },
  { src: datalignadvisoryLogo.url, width: 240, height: 48, alt: "Datalign Advisory logo" },
  { src: nationwidepowerLogo.url, width: 240, height: 183, alt: "Nationwide Power logo" },
  { src: posterityhealthLogo.url, width: 240, height: 32, alt: "Posterity Health logo" },
  { src: relaycommerceLogo.url, width: 240, height: 72, alt: "Relay Commerce logo" },
  { src: seraphicsecurityLogo.url, width: 240, height: 47, alt: "Seraphic Security logo" },
  {
    src: uniforms2youLogo.url,
    width: 300,
    height: 96,
    alt: "Uniforms 2 You logo",
    imgClass: "h-20 max-w-[300px]",
  },
];


export function LogoRotator({ heading = "Trusted by companies like:" }: { heading?: string }) {
  const verifiedLogos = LOGOS.filter((logo) => logo.permissionDate);
  // The marquee is held still until the page has finished loading: constant
  // pixel change keeps Speed Index high even after the page looks done.
  // Visitors who ask for reduced motion get a wrapped static strip instead:
  // every logo stays visible, nothing animates, and no logo is clipped by
  // the overflow-hidden marquee viewport.
  const [animate, setAnimate] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const reduced =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setReducedMotion(true);
      return;
    }

    let timer: number | undefined;
    const start = () => {
      timer = window.setTimeout(() => setAnimate(true), 400);
    };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });

    return () => {
      window.removeEventListener("load", start);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  // Duplicate the list so the marquee can loop seamlessly; unnecessary for
  // the static wrapped layout.
  const items = reducedMotion ? verifiedLogos : [...verifiedLogos, ...verifiedLogos];

  if (verifiedLogos.length === 0) return null;

  return (
    <div className="w-full">
      <p className="text-center text-sm text-muted-foreground font-medium">
        {heading}
      </p>
      {reducedMotion ? (
        <div className="mx-auto mt-8 flex max-w-5xl flex-wrap items-center justify-center gap-x-12 gap-y-6">
          {items.map((logo) => (
            <div
              key={logo.alt}
              className="flex h-20 shrink-0 items-center justify-center px-2"
            >
              <img
                src={logo.src}
                alt={logo.alt}
                width={logo.width}
                height={logo.height}
                className={
                  logo.imgClass ??
                  "h-14 w-auto max-w-[240px] object-contain"
                }
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="relative mt-8 overflow-hidden">
          {/* Fade masks on each side */}
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-28 bg-gradient-to-r from-paper to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-28 bg-gradient-to-l from-paper to-transparent" />

          <div
            className={`flex w-max items-center gap-14 pr-14 ${
              animate ? "animate-logo-marquee" : ""
            }`}
          >
            {items.map((logo, i) => (
              <div
                key={`${logo.alt}-${i}`}
                className="flex h-20 shrink-0 items-center justify-center px-2"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  className={
                    logo.imgClass ??
                    "h-14 w-auto max-w-[240px] object-contain"
                  }
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
